(() => {
  function init(scope = document) {
    scope.querySelectorAll('[data-room-gallery]').forEach(root => {
      if (root.dataset.galleryReady) return;
      const track = root.querySelector('[data-gallery-track]');
      const cards = [...track.children];
      if (cards.length < 2) return;
      root.dataset.galleryReady = 'true';
      const controls = root.querySelector('[data-gallery-controls]');
      const position = root.querySelector('[data-gallery-position]');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const events = new AbortController();
      const listen = (node, type, fn, options = {}) => node.addEventListener(type, fn, {...options, signal: events.signal});
      let start = 0, step = 0, timer, index = 0, frame = 0, phase = 0, target = 0, touching = false, nearby = false;
      const wrap = value => ((value % cards.length) + cards.length) % cards.length;
      const clone = card => {
        const copy = card.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a,button,[tabindex]').forEach(node => node.tabIndex = -1);
        copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        copy.removeAttribute('id');
        return copy;
      };
      const count = Math.min(4, cards.length);
      const before = cards.slice(-count).map(clone), after = cards.slice(0, count).map(clone);
      track.prepend(...before); track.append(...after);
      const allCards = [...track.children];
      const warmed = new WeakSet();
      const jump = left => track.scrollTo({left, behavior: 'instant'});
      const render = value => jump(start + wrap(value) * step);
      function warm(value) {
        if (!nearby) return;
        const selected = new Set([-1, 0, 1, 2, 3].map(offset => wrap(Math.round(value) + offset)));
        allCards.forEach((card, physical) => {
          if (!selected.has(wrap(physical - count))) return;
          const img = card.querySelector('img');
          if (!img || warmed.has(img)) return;
          warmed.add(img); img.loading = 'eager'; img.decoding = 'async';
          img.decode?.().catch(() => {});
        });
      }
      function update(value) {
        index = wrap(Math.round(value));
        position.textContent = `${String(index + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
        position.setAttribute('aria-label', `Room ${index + 1} of ${cards.length}`);
        warm(index);
      }
      function cancel() { cancelAnimationFrame(frame); frame = 0; clearTimeout(timer); }
      function measure() {
        const destination = frame ? wrap(Math.round(target)) : index;
        cancel();
        const first = cards[0].getBoundingClientRect();
        start = first.left - track.getBoundingClientRect().left + track.scrollLeft;
        step = cards[1].getBoundingClientRect().left - first.left;
        phase = target = destination; render(destination); update(destination);
      }
      function settle() {
        if (!step || frame || touching) return;
        const nearest = wrap(Math.round((track.scrollLeft - start) / step));
        if (Math.abs(track.scrollLeft - (start + nearest * step)) > .5) render(nearest);
        phase = target = nearest; update(nearest);
      }
      function move(direction) {
        if (!step) return;
        if (!frame) phase = target = (track.scrollLeft - start) / step;
        target = Math.round(target) + direction;
        cancel(); touching = false; nearby = true; warm(target);
        if (reduced.matches) { phase = target = wrap(target); render(target); update(target); return; }
        // Logical movement remains unbounded; physical scroll stays within one repeated cycle.
        // Rapid requests preserve their destination without reaching the browser's scroll limit.
        const from = phase, to = target, began = performance.now();
        const duration = Math.min(520, 320 + Math.abs(to - from) * 12);
        function tick(now) {
          const progress = Math.min(1, (now - began) / duration);
          phase = from + (to - from) * (1 - Math.pow(1 - progress, 3));
          render(phase);
          if (progress < 1) frame = requestAnimationFrame(tick);
          else { frame = 0; phase = target = wrap(to); render(target); update(target); }
        }
        frame = requestAnimationFrame(tick);
      }
      listen(root.querySelector('[data-gallery-prev]'), 'click', () => move(-1));
      listen(root.querySelector('[data-gallery-next]'), 'click', () => move(1));
      listen(track, 'scroll', () => { if (frame) return; clearTimeout(timer); timer = setTimeout(settle, 180); }, {passive: true});
      listen(track, 'scrollend', settle);
      listen(track, 'pointerdown', () => { cancel(); touching = true; }, {passive: true});
      const release = () => { if (touching) { touching = false; clearTimeout(timer); timer = setTimeout(settle, 180); } };
      listen(document, 'pointerup', release, {passive: true});
      listen(track, 'pointercancel', release, {passive: true});
      listen(track, 'wheel', () => { cancel(); touching = false; }, {passive: true});
      listen(track, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          cancel(); touching = false; phase = target = event.key === 'Home' ? 0 : cards.length - 1;
          render(target); update(target);
        } else move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      listen(track, 'focusin', event => {
        const card = cards.find(candidate => candidate.contains(event.target));
        if (!card) return;
        const rect = card.getBoundingClientRect(), bounds = track.getBoundingClientRect();
        if (rect.left < bounds.left || rect.right > bounds.right) {
          cancel(); phase = target = cards.indexOf(card); render(target); update(target);
        }
      });
      const enquiry = root.querySelector('[data-gallery-enquiry]');
      if (enquiry) listen(enquiry, 'click', event => {
        const form = document.getElementById('project-enquiry'); if (!form) return;
        event.preventDefault(); form.scrollIntoView({behavior: reduced.matches ? 'instant' : 'smooth', block: 'start'});
        const heading = form.querySelector('h2'); heading.tabIndex = -1; heading.focus({preventScroll: true});
      });
      listen(reduced, 'change', () => {
        if (reduced.matches && frame) { cancel(); phase = target = wrap(target); render(target); update(target); }
      });
      const proximity = new IntersectionObserver(entries => {
        nearby = entries.some(entry => entry.isIntersecting); if (nearby) warm(index);
      }, {rootMargin: '360px 0px'});
      proximity.observe(root);
      let geometry = '';
      const resize = new ResizeObserver(() => {
        const next = `${track.clientWidth}:${cards[0].getBoundingClientRect().width}`;
        if (geometry !== next) { geometry = next; measure(); }
      });
      resize.observe(track); resize.observe(cards[0]);
      controls.hidden = false; measure();
      document.fonts?.ready.then(() => { if (root.isConnected) measure(); });
      listen(document, 'shopify:section:unload', event => {
        if (event.target.contains(root)) { cancel(); resize.disconnect(); proximity.disconnect(); events.abort(); }
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true}); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
